import { dinheiro, expressivo, potencia } from '../nucleo/numero.js';
import { Ressalvas } from './Ressalvas.jsx';
import { SeloDeConfianca } from './SeloDeConfianca.jsx';
import { VerAConta } from './VerAConta.jsx';

/**
 * A resposta.
 *
 * A ordem da tela é uma decisão de produto, não de layout:
 *
 * 1. **O selo de confiança vem antes do número.** Invertido, o leitor já
 *    decidiu o que achar do resultado quando descobre que ele é estimativa de
 *    tabela. A procedência não é rodapé.
 * 2. **O dinheiro é o maior, mas não está sozinho.** "Quanto economizo" é a
 *    pergunta que o produtor faz, e fingir que ele quer metros cúbicos é
 *    falar a língua do laboratório. Ainda assim o gás e os kWh ficam ao lado,
 *    porque é deles que o dinheiro depende: quem vê só o real não percebe que
 *    trocar a tarifa muda tudo.
 * 3. **A leitura em frases é o corpo, não um resumo.** Ela vem pronta da API,
 *    já na língua de quem cria animal, e é a mesma narrativa que o consultor
 *    vai ver — um número só nos dois lugares.
 * 4. **O convite para analisar o dejeto aparece quando o potencial é de
 *    tabela.** É aqui que o app manda gente para o laboratório em vez de
 *    competir com ele.
 */

//: O que o lado da faixa muda para quem lê — e só entra no convite porque
//: muda a AÇÃO, não porque é informação a mais.
//:
//: Medido no catálogo: em cinco dos seis materiais com faixa o nosso valor
//: fica na parte de cima dela, e em três ele chega ao teto — e nesses três é
//: literalmente o maior valor publicado, sem arredondamento. Quem vê "até 125%
//: de diferença" sem o lado imagina que o erro pode ir para os dois lados; no
//: caso da cama de aviário ele só pode ir para baixo. E o bovino de corte é o contrário — a boa notícia que nenhuma
//: calculadora de biogás dá, porque ninguém escreve o elogio da própria
//: ressalva.
//: `teto` afirma identidade, `perto_do_teto` afirma vizinhança — e a diferença
//: não é preciosismo: o suíno de terminação fica a 85% da faixa, e chamar 303
//: de "o maior" quando o maior é 313 é a mesma mentira de medida que a ressalva
//: do ">85%" cometia.
//: A frase que o convite existe para dizer — e que até 15/10/2026 ele dizia
//: sempre, sozinha e em primeiro lugar, inclusive para quem tinha uma resposta
//: de graça na mão. Ela não saiu: continua verdadeira, e é o que torna o
//: estudo financiável. Mora numa constante porque agora ocupa UMA de duas
//: posições, e o defeito era a posição e não o texto. Duas cópias nos dois
//: ramos do ternário deixariam a próxima pessoa corrigir uma e esquecer a
//: outra — foi assim que a ressalva passou a dizer 65% com o convite dizendo
//: 25% sobre o mesmo bovino.
const FRASE_DO_ENSAIO =
  'Um ensaio de potencial de metano no seu próprio dejeto é o que transforma esta conta numa que o banco aceita.';

//: A legenda do número grande, por regime — e esta é a correção de uma
//: afirmação falsa, não um ajuste de texto.
//:
//: Até o #016 a tela escrevia "em energia elétrica que você deixa de comprar"
//: debaixo do `economia_ano` SEMPRE. Numa granja de terminação de 4.000
//: cabeças a geração é 11,6× o consumo elétrico que a Embrapa/CIAS publica
//: para a mesma tipologia: 91% daquele dinheiro não é conta de luz que deixa
//: de chegar, é crédito injetado na rede, que a Lei 14.300/2022 paga a 70% da
//: tarifa e que vence em 60 meses. A legenda antiga vale num dos três regimes.
//:
//: A palavra vem do `meta.regime_da_energia`, decidida em `_regime_da_energia`
//: no Python. A tela não recalcula: as duas pernas nascem da mesma subtração,
//: e foi ter duas réguas para a mesma frase que fez a ressalva dizer 65%
//: enquanto o convite dizia 25%.
const ROTULO_DA_ENERGIA = {
  autoconsumo: 'por ano em energia elétrica que você deixa de comprar',
  excedente: 'por ano em crédito pela energia que você injeta na rede',
  misto: 'por ano: parte na sua conta de luz, parte em crédito na rede',
};

//: Envelope de uma versão que não rateava, ou palavra que eu não conheço. A
//: saída tem de ser a frase que não pode estar errada — nunca a do
//: autoconsumo, que é justamente a afirmação sem respaldo. Um `??` apontando
//: para a legenda antiga transformaria o fallback em reincidência.
const ROTULO_DA_ENERGIA_SEM_REGIME = 'por ano com a energia que o biodigestor gera';

const LADO_DA_FAIXA = {
  teto: 'O número desta tela é o maior que alguém já publicou para este material, e é o do seu dejeto que o banco vai querer. ',
  perto_do_teto: 'O número desta tela está entre os maiores dessa faixa, e é o do seu dejeto que o banco vai querer. ',
  piso: 'O número desta tela é o menor já publicado para este material: medir pode aumentar o seu projeto, não só reduzir. ',
  perto_do_piso: 'O número desta tela está entre os menores dessa faixa: medir pode aumentar o seu projeto, não só reduzir. ',
};

function Destaque({ valor, unidade, rotulo, principal = false }) {
  if (valor === undefined || valor === null) return null;
  return (
    <div className={`destaque ${principal ? 'destaque--principal' : ''}`}>
      <p className="destaque__valor">
        <span className="numero-grande">{valor}</span>
        {unidade ? <span className="unidade"> {unidade}</span> : null}
      </p>
      <p className="destaque__rotulo">{rotulo}</p>
    </div>
  );
}

export function Resultado({ envelope, onPedirAnalise }) {
  if (!envelope) return null;
  const { resultado = {}, leitura = [], avisos = [], memoria = [], premissas = [], meta = {}, confianca } =
    envelope;

  const deTabela = meta.potencial_de_tabela === true;
  // Quanto a literatura varia para este material. Já veio calculado: este
  // texto cravava 25%, que é o espalhamento do suíno, e bovino de corte abriu
  // para 65% — duas frases da mesma tela discordando sobre o mesmo dejeto.
  //
  // Sem `|| null`: quem decide é o ternário lá embaixo, e para ele 0, null e
  // `undefined` são a mesma coisa — cai na frase sem número, de propósito.
  // "chega a 0% de diferença" e "chega a undefined%" são piores que não dizer.
  // O guarda estava aqui por reflexo, e quebrar o código de propósito provou
  // que ele não guardava nada: tirá-lo não fazia nenhum teste cair.
  const espalhamento = meta.espalhamento_do_potencial_pct;
  // De que lado da faixa sobrou diferença. Vem como PALAVRA e não como número
  // justamente para a tela não ter os seus próprios limiares: o Python decide
  // em `_lado_da_faixa` e aqui só se escolhe a frase. Dois lugares com a mesma
  // régua é como a ressalva passou a dizer 65% e o convite 25%.
  const lado = meta.lado_da_faixa_do_potencial;
  // De que o número grande é feito. Mesma razão de vir como palavra: a
  // fronteira é o zero de cada perna, e as pernas são calculadas no Python.
  const rotuloDaEnergia =
    ROTULO_DA_ENERGIA[meta.regime_da_energia] ?? ROTULO_DA_ENERGIA_SEM_REGIME;
  // A informação de graça que estreita a faixa deste material, quando o
  // catálogo tem uma. Vem inteira da API — pergunta, covariável e efeito — e
  // não montada aqui: tela que escreve a própria pergunta volta a ter duas
  // réguas. O `efeito` carrega número medido de propósito, porque "contar os
  // lotes ajuda" é incentivo e "6 lotes dão 159,2 e 12 dão 70,9" é informação.
  //
  // Só existe para dois dos seis materiais com faixa, e isso é medido, não
  // esquecido: nos outros quatro ou a covariável está confundida com o eixo de
  // estocagem que o app já modela, ou os laboratórios discordam de verdade. O
  // motivo de cada `None` está escrito na `observacao` do catálogo.
  const pergunta = meta.pergunta_antes_do_ensaio;
  const gerador = potencia(resultado.potencia_kw);

  return (
    <div className="resultado empilha-largo">
      <section className="cartao resultado__cabeca" aria-labelledby="titulo-resultado">
        <SeloDeConfianca confianca={confianca} />

        <h2 id="titulo-resultado" className="so-leitor">
          Resultado da estimativa
        </h2>

        <Destaque
          principal
          valor={dinheiro(resultado.economia_ano)}
          rotulo={rotuloDaEnergia}
        />

        <div className="resultado__secundarios">
          <Destaque
            valor={expressivo(resultado.metano_dia)}
            unidade="Nm³/dia"
            rotulo="de metano"
          />
          {/* `expressivo`, e não `inteiro`: a leitura logo abaixo diz "9,6 kWh
              de energia elétrica por dia" e o cartão dizia "10". Mesma
              grandeza, dois números na mesma tela — o produtor conclui que
              são coisas diferentes, que é exatamente o que o módulo de número
              existe para impedir. */}
          <Destaque
            valor={expressivo(resultado.energia_eletrica_dia)}
            unidade="kWh/dia"
            rotulo="de energia elétrica"
          />
          <Destaque
            valor={gerador.valor}
            unidade={gerador.unidade}
            rotulo="de gerador a instalar"
          />
        </div>

        {confianca ? <p className="resultado__porque">{confianca.porque}.</p> : null}
      </section>

      {leitura.length ? (
        <section className="bloco" aria-labelledby="titulo-leitura">
          <h2 id="titulo-leitura" className="rotulo-secao">
            O que isso quer dizer
          </h2>
          <ol className="leitura">
            {leitura.map((linha, i) => (
              <li key={i} className="leitura__linha">
                {linha}
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <Ressalvas avisos={avisos} />

      {deTabela ? (
        <section className="cartao cartao--raso convite">
          <h2 className="convite__titulo">O número acima é uma estimativa de tabela</h2>
          <p className="convite__texto">
            {espalhamento
              ? `A faixa da literatura para este tipo de dejeto chega a ${espalhamento}% de diferença entre o menor e o maior valor publicado`
              : 'A faixa da literatura para este tipo de dejeto é larga'}
            {' '}— e o seu material tem o seu manejo, a sua ração e a sua
            diluição.{' '}
            {/* Sem `|| ''`: um objeto sem a chave devolve `undefined`, e o JSX
                não renderiza `undefined` como texto — ao contrário da template
                string do espalhamento acima, onde `undefined` virava a palavra
                "undefined" na tela. É a mesma ausência com dois destinos, e
                guardar aqui o que não precisa de guarda foi o que fez o
                `|| null` da linha de cima parecer defesa sem defender. */}
            {LADO_DA_FAIXA[lado]}
            {pergunta
              ? 'Mas parte dessa diferença não é discordância entre laboratórios: é uma coisa que você já sabe e esta conta nunca perguntou.'
              : FRASE_DO_ENSAIO}
          </p>
          {/* A pergunta não tem campo para resposta, e isso é de propósito: o
              app ainda não sabe recalcular com ela, e botão que não recalcula
              promete o que não entrega. Enquanto isso, o `efeito` já diz
              quanto vale cada resposta — quem lê "6 lotes dão 159,2 e 12 dão
              70,9" descobre sozinho que o número desta tela é o da cama nova,
              sem pagar nada e sem esperar 30 dias. */}
          {pergunta ? (
            <div className="convite__gratis">
              <p className="convite__pergunta">{pergunta.pergunta}</p>
              <p className="convite__efeito">{pergunta.efeito}</p>
              <p className="convite__texto">
                Responder não custa nada, e vem antes do laboratório: um ensaio
                feito sem registrar isso herda a mesma dúvida que ele foi pago
                para tirar. {FRASE_DO_ENSAIO}
              </p>
            </div>
          ) : null}
          {onPedirAnalise ? (
            <button type="button" className="botao" onClick={onPedirAnalise}>
              Quero analisar o meu dejeto
            </button>
          ) : null}
        </section>
      ) : null}

      <VerAConta memoria={memoria} premissas={premissas} meta={meta} />
    </div>
  );
}
